import React, { useState, useEffect } from "react";
import { get, post, del, patch } from "../../common/apiClient";
import { toast } from "react-toastify";

const ADMIN_API_KEY =
  process.env.REACT_APP_ADMIN_API_KEY || "698ee7bed55acd7e57bc4400";

const DomainAdminPage = () => {
  const [domains, setDomains] = useState([]);
  const [name, setName] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setLoadingList(true);
    try {
      const res = await get("/admin/domains");
      const data = res.data || res;
      setDomains(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch domains failed", err);
      toast.error("Không thể tải danh sách domain");
    } finally {
      setLoadingList(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Vui lòng nhập domain");
      return;
    }
    setLoading(true);
    try {
      const body = { name: name.trim() };
      if (zoneId.trim()) {
        body.zoneId = zoneId.trim();
      }
      await post("/admin/domains", body);
      toast.success("Tạo domain thành công");
      setName("");
      setZoneId("");
      fetchDomains();
    } catch (err) {
      console.error("Create domain failed", err);
      const msg =
        err.response?.data?.message ||
        (err.response?.data?.errors &&
          err.response.data.errors.map((e) => e.msg).join(", ")) ||
        "Tạo domain thất bại";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (domainId) => {
    setVerifyingId(domainId);
    try {
      await post(`/admin/domains/${domainId}/verify`, {});
      toast.success("Xác minh domain thành công");
      fetchDomains();
    } catch (err) {
      console.error("Verify domain failed", err);
      const msg =
        err.response?.data?.message ||
        "Xác minh domain thất bại. Vui lòng kiểm tra DNS TXT record.";
      toast.error(msg);
    } finally {
      setVerifyingId(null);
    }
  };

  const handleToggleVerified = async (domainId) => {
    setTogglingId(domainId);
    try {
      await patch(`/admin/domains/${domainId}/toggle-verified`, {});
      toast.success("Cập nhật trạng thái domain thành công");
      fetchDomains();
    } catch (err) {
      console.error("Toggle verified failed", err);
      const msg = err.response?.data?.message || "Cập nhật trạng thái thất bại";
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (domainId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa domain này?")) {
      return;
    }
    setDeletingId(domainId);
    try {
      await del(`/admin/domains/${domainId}`, {});
      toast.success("Xóa domain thành công");
      fetchDomains();
    } catch (err) {
      console.error("Delete domain failed", err);
      const msg = err.response?.data?.message || "Xóa domain thất bại";
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-md p-6 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Quản lý Domain Temp Mail
            </h1>
            <p className="text-sm text-gray-600 mt-2">
              Trang này chỉ dành cho admin. Sử dụng để thêm domain mới, xác minh
              và quản lý domain.
            </p>
          </div>

          {/* Form thêm domain */}
          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Thêm domain mới
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ví dụ: example.com"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zone ID (Cloudflare)
                </label>
                <input
                  type="text"
                  value={zoneId}
                  onChange={(e) => setZoneId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ví dụ: fa63097641b2588ae1f03330a24daecc"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Tùy chọn: Zone ID của Cloudflare để tự động tạo DNS records
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Đang tạo..." : "Thêm domain"}
              </button>
            </form>
          </div>

          {/* Danh sách domain */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Danh sách domain ({domains.length})
            </h2>
            {loadingList ? (
              <div className="text-center py-8 text-gray-500">Đang tải...</div>
            ) : domains.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Chưa có domain nào
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Domain
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ngày tạo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {domains.map((domain) => (
                      <tr key={domain._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {domain.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {domain.verified ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Đã xác minh
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Chưa xác minh
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {domain.createdAt
                            ? new Date(domain.createdAt).toLocaleDateString(
                                "vi-VN",
                              )
                            : "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {/* {!domain.verified && (
                                                            <button
                                                                onClick={() => handleVerify(domain._id)}
                                                                disabled={verifyingId === domain._id}
                                                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {verifyingId === domain._id
                                                                    ? "Đang xác minh..."
                                                                    : "Xác minh"}
                                                            </button>
                                                        )} */}
                            <button
                              onClick={() => handleToggleVerified(domain._id)}
                              disabled={togglingId === domain._id}
                              className={`${
                                domain.verified
                                  ? "text-orange-600 hover:text-orange-900"
                                  : "text-green-600 hover:text-green-900"
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {togglingId === domain._id
                                ? "Đang cập nhật..."
                                : domain.verified
                                  ? "Bỏ active"
                                  : "Active"}
                            </button>
                            <button
                              onClick={() => handleDelete(domain._id)}
                              disabled={deletingId === domain._id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deletingId === domain._id
                                ? "Đang xóa..."
                                : "Xóa"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainAdminPage;
